import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Alert } from 'react-bootstrap';
// New package button
import { Button } from '@veeyaainnovatives/button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/images/sds-logo.svg';
import customerPortalImage from '../assets/images/customer-portal.png';
import axios from 'axios';
import { getApiUrl } from '../config/api';

const CustomerLogin = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
    const [forgotPasswordError, setForgotPasswordError] = useState('');
    const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState('');
    const [isSubmittingForgotPassword, setIsSubmittingForgotPassword] = useState(false);
    
    const { login, isAuthenticated } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        // Redirect if already authenticated
        if (isAuthenticated) {
            navigate('/customer/dashboard');
        }
    }, [isAuthenticated, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setFieldErrors({});
        setIsSubmitting(true);

        if (!username || !password) {
            setError('Please enter both username and password');
            setIsSubmitting(false);
            return;
        }

        const result = await login(username, password);
        
        if (result.success) {
            navigate('/customer/dashboard');
        } else {
            setError(result.message || 'Login failed. Please check your credentials.');
            // Set field-specific errors if available
            if (result.errors) {
                setFieldErrors(result.errors);
            }
        }
        
        setIsSubmitting(false);
    };

    const handleForgotPasswordSubmit = async (e) => {
        e.preventDefault();
        setForgotPasswordError('');
        setForgotPasswordSuccess('');
        setIsSubmittingForgotPassword(true);

        if (!forgotPasswordEmail.trim()) {
            setForgotPasswordError('Please enter your email address');
            setIsSubmittingForgotPassword(false);
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(forgotPasswordEmail)) {
            setForgotPasswordError('Please enter a valid email address');
            setIsSubmittingForgotPassword(false);
            return;
        }

        try {
            // Call reset password API
            const response = await axios.post(
                getApiUrl('api/public/customer/reset-password'),
                {
                    email: forgotPasswordEmail.trim()
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                }
            );

            // Handle success response
            if (response.data.success) {
                setForgotPasswordSuccess(
                    response.data.message || 
                    'Password reset link has been sent to your email address. Please check your inbox.'
                );
                setForgotPasswordEmail('');
                
                // Auto hide success message after 5 seconds
                setTimeout(() => {
                    setForgotPasswordSuccess('');
                }, 5000);
            } else {
                setForgotPasswordError(
                    response.data.message || 
                    'Failed to send password reset link. Please try again later.'
                );
            }
        } catch (error) {
            console.error('Forgot password error:', error);
            
            // Handle error response
            if (error.response) {
                const { status, data } = error.response;
                
                if (status === 404) {
                    setForgotPasswordError(
                        data.message || 
                        'Email address not found. Please check and try again.'
                    );
                } else if (status === 422) {
                    // Validation errors
                    if (data.errors && data.errors.email) {
                        setForgotPasswordError(
                            Array.isArray(data.errors.email) 
                                ? data.errors.email[0] 
                                : data.errors.email
                        );
                    } else {
                        setForgotPasswordError(
                            data.message || 
                            'Please enter a valid email address.'
                        );
                    }
                } else if (status === 429) {
                    setForgotPasswordError(
                        'Too many requests. Please wait a few minutes before trying again.'
                    );
                } else {
                    setForgotPasswordError(
                        data.message || 
                        'Failed to send password reset link. Please try again later.'
                    );
                }
            } else if (error.request) {
                setForgotPasswordError(
                    'Network error. Please check your connection and try again.'
                );
            } else {
                setForgotPasswordError(
                    'An error occurred. Please try again later.'
                );
            }
        } finally {
            setIsSubmittingForgotPassword(false);
        }
    };

    const handleToggleForgotPassword = () => {
        setShowForgotPassword(!showForgotPassword);
        setError('');
        setForgotPasswordError('');
        setForgotPasswordSuccess('');
        setForgotPasswordEmail('');
        setUsername('');
        setPassword('');
    };

    return (
        <div className="customer-login-wrapper d-flex" style={{ 
            overflow: 'hidden',
            height: '100vh',
            maxHeight: '100vh',
            position: 'relative'
        }}>
            {/* Left Section - Background Image with Overlay */}
            <div className="d-none d-lg-flex flex-column justify-content-end" style={{
                width: '60%',
                backgroundImage: `url(${customerPortalImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                padding: '60px',
                paddingBottom: '30px',
                height: '100vh',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Full Black Overlay for Left Side */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    zIndex: 0
                }}></div>

                {/* Text Content - No Background Card */}
                <div style={{ 
                    maxWidth: '600px', 
                    position: 'relative', 
                    zIndex: 2,
                    marginBottom: '0'
                }}>
                    {/* Simple Catch Words - Matching Home Slider Style */}
                    <h1 style={{ 
                        color: '#fff',
                        fontSize: '46px',
                        fontWeight: 'bold',
                        marginBottom: '20px',
                        lineHeight: '74px',
                        textAlign: 'left'
                    }}>
                        Welcome to <strong style={{ color: '#e7b06d' }}>Sathiesh Design Studio</strong>
                    </h1>
                    <p style={{ 
                        color: '#fff',
                        fontSize: '20px',
                        fontWeight: '400',
                        marginBottom: '15px',
                        lineHeight: '34px',
                        textAlign: 'left'
                    }}>
                        <strong style={{ color: '#e7b06d' }}>Track Your Projects</strong>
                    </p>
                    <p style={{ 
                        color: '#fff',
                        fontSize: '20px',
                        fontWeight: '400',
                        lineHeight: '34px',
                        textAlign: 'left'
                    }}>
                        Monitor progress • View documents • Stay connected
                    </p>
                </div>
            </div>

            {/* Right Section - Green Background with Logo and Login Form */}
            <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center" style={{
                backgroundColor: '#677a58',
                padding: '20px',
                position: 'relative',
                overflow: 'hidden',
                height: '100vh',
                maxHeight: '100vh'
            }}>
                {/* Decorative circles */}
                <div style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(215, 228, 213, 0.1)',
                    zIndex: 0
                }}></div>
                <div style={{
                    position: 'absolute',
                    bottom: '50px',
                    right: '30px',
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(231, 176, 109, 0.15)',
                    zIndex: 0
                }}></div>

                <div style={{ 
                    width: '100%',
                    maxWidth: '450px',
                    position: 'relative',
                    zIndex: 1,
                    maxHeight: '100%',
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {/* Logo */}
                    <div className="text-center" style={{ marginBottom: '20px', flexShrink: 0 }}>
                        <img 
                            src={logo} 
                            alt="SDS Logo" 
                            style={{ 
                                height: '60px', 
                                marginBottom: '20px',
                                maxWidth: '200px'
                            }} 
                        />
                    </div>

                    <div style={{ padding: '20px', flex: '1', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <h2 className="text-white mb-3" style={{ 
                            fontWeight: 'bold',
                            fontSize: '28px',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            marginBottom: '20px'
                        }}>
                            {showForgotPassword ? 'FORGOT PASSWORD' : 'LOGIN'}
                        </h2>

                        {error && (
                            <Alert variant="danger" className="mb-3" style={{ 
                                borderRadius: '10px',
                                border: 'none',
                                backgroundColor: '#f8d7da',
                                color: '#721c24'
                            }}>
                                {error}
                            </Alert>
                        )}

                        {forgotPasswordError && (
                            <Alert variant="danger" className="mb-3" style={{ 
                                borderRadius: '10px',
                                border: 'none',
                                backgroundColor: '#f8d7da',
                                color: '#721c24'
                            }}>
                                {forgotPasswordError}
                            </Alert>
                        )}

                        {forgotPasswordSuccess && (
                            <Alert variant="success" className="mb-3" style={{ 
                                borderRadius: '10px',
                                border: 'none',
                                backgroundColor: '#d4edda',
                                color: '#155724'
                            }}>
                                {forgotPasswordSuccess}
                            </Alert>
                        )}

                        {showForgotPassword ? (
                            // Forgot Password Form
                            <Form onSubmit={handleForgotPasswordSubmit} style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
                                <div className="mb-4" style={{ color: '#d7e4d5', fontSize: '14px', lineHeight: '1.6' }}>
                                    <p className="mb-0">
                                        Enter your email address and we'll send you a link to reset your password.
                                    </p>
                                </div>

                                <Form.Group className="mb-4">
                                    <Form.Label className="text-white mb-2" style={{ fontSize: '14px', fontWeight: '500' }}>
                                        Email Address
                                    </Form.Label>
                                    <Form.Control
                                        type="email"
                                        placeholder="Enter your email address"
                                        value={forgotPasswordEmail}
                                        onChange={(e) => {
                                            setForgotPasswordEmail(e.target.value);
                                            setForgotPasswordError('');
                                        }}
                                        required
                                        disabled={isSubmittingForgotPassword}
                                        style={{ 
                                            borderRadius: '10px', 
                                            border: 'none', 
                                            padding: '14px 18px',
                                            fontSize: '15px',
                                            backgroundColor: '#d7e4d5',
                                            color: '#677a58',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onFocus={(e) => {
                                            e.currentTarget.style.backgroundColor = '#fff';
                                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(215, 228, 213, 0.5)';
                                        }}
                                        onBlur={(e) => {
                                            e.currentTarget.style.backgroundColor = '#d7e4d5';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                    />
                                </Form.Group>

                                <div className="mb-3">
                                    <Button
                                        type="submit"
                                        className="w-100"
                                        disabled={isSubmittingForgotPassword}
                                        backgroundColor="#677a58"
                                        color="#ffffff"
                                        hoverBackgroundColor="#5a6a4a"
                                        style={{
                                            backgroundColor: '#677a58',
                                            color: '#ffffff',
                                            border: 'none',
                                            borderRadius: '10px',
                                            height: '45px',
                                            fontSize: '15px',
                                            fontWeight: '600',
                                            textTransform: 'uppercase',
                                            letterSpacing: '1px',
                                            width: '100%',
                                            marginBottom: '15px',
                                            cursor: isSubmittingForgotPassword ? 'not-allowed' : 'pointer',
                                            opacity: isSubmittingForgotPassword ? 0.7 : 1,
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isSubmittingForgotPassword) {
                                                e.currentTarget.style.backgroundColor = '#5a6a4a';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isSubmittingForgotPassword) {
                                                e.currentTarget.style.backgroundColor = '#677a58';
                                            }
                                        }}
                                    >
                                        {isSubmittingForgotPassword ? 'Sending...' : 'Send Reset Link'}
                                    </Button>

                                    <button
                                        type="button"
                                        onClick={handleToggleForgotPassword}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#d7e4d5',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            padding: '10px 0',
                                            cursor: 'pointer',
                                            textDecoration: 'none',
                                            width: '100%',
                                            textAlign: 'center',
                                            transition: 'color 0.3s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.color = '#e7b06d';
                                            e.currentTarget.style.textDecoration = 'underline';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.color = '#d7e4d5';
                                            e.currentTarget.style.textDecoration = 'none';
                                        }}
                                    >
                                        <i className="fa fa-arrow-left"></i>
                                        Back to Login
                                    </button>
                                </div>
                            </Form>
                        ) : (
                            // Login Form
                            <Form onSubmit={handleSubmit} style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
                            <Form.Group className="mb-3">
                                <Form.Label className="text-white mb-2" style={{ fontSize: '14px', fontWeight: '500' }}>
                                    Username
                                </Form.Label>
                                <Form.Control
                                        type="text"
                                        placeholder="Enter your username"
                                        value={username}
                                        onChange={(e) => {
                                            setUsername(e.target.value);
                                            // Clear field error when user types
                                            if (fieldErrors.username) {
                                                setFieldErrors(prev => {
                                                    const newErrors = { ...prev };
                                                    delete newErrors.username;
                                                    return newErrors;
                                                });
                                            }
                                        }}
                                        required
                                        disabled={isSubmitting}
                                        isInvalid={!!fieldErrors.username}
                                        style={{ 
                                            borderRadius: '10px', 
                                            border: fieldErrors.username ? '2px solid #dc3545' : 'none', 
                                            padding: '14px 18px',
                                            fontSize: '15px',
                                            backgroundColor: '#d7e4d5',
                                            color: '#677a58',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onFocus={(e) => {
                                            e.currentTarget.style.backgroundColor = '#fff';
                                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(215, 228, 213, 0.5)';
                                        }}
                                        onBlur={(e) => {
                                            e.currentTarget.style.backgroundColor = '#d7e4d5';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                    />
                                {fieldErrors.username && (
                                    <Form.Control.Feedback type="invalid" style={{ display: 'block', color: '#f8d7da', fontSize: '13px', marginTop: '5px' }}>
                                        {Array.isArray(fieldErrors.username) ? fieldErrors.username[0] : fieldErrors.username}
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="text-white mb-2" style={{ fontSize: '14px', fontWeight: '500' }}>
                                    Password
                                </Form.Label>
                                <div style={{ position: 'relative' }}>
                                    <Form.Control
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="Enter your password"
                                            value={password}
                                            onChange={(e) => {
                                                setPassword(e.target.value);
                                                // Clear field error when user types
                                                if (fieldErrors.password) {
                                                    setFieldErrors(prev => {
                                                        const newErrors = { ...prev };
                                                        delete newErrors.password;
                                                        return newErrors;
                                                    });
                                                }
                                            }}
                                            required
                                            disabled={isSubmitting}
                                            isInvalid={!!fieldErrors.password}
                                            style={{ 
                                                borderRadius: '10px', 
                                                border: fieldErrors.password ? '2px solid #dc3545' : 'none', 
                                                padding: '14px 18px',
                                                paddingRight: '50px',
                                                fontSize: '15px',
                                                backgroundColor: '#d7e4d5',
                                                color: '#677a58',
                                                transition: 'all 0.3s ease'
                                            }}
                                            onFocus={(e) => {
                                                e.currentTarget.style.backgroundColor = '#fff';
                                                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(215, 228, 213, 0.5)';
                                            }}
                                            onBlur={(e) => {
                                                e.currentTarget.style.backgroundColor = '#d7e4d5';
                                                e.currentTarget.style.boxShadow = 'none';
                                            }}
                                        />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{
                                            position: 'absolute',
                                            right: '15px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            border: 'none',
                                            background: 'none',
                                            color: '#677a58',
                                            cursor: 'pointer',
                                            fontSize: '18px',
                                            padding: '5px'
                                        }}
                                    >
                                        <span className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></span>
                                    </button>
                                </div>
                                {fieldErrors.password && (
                                    <Form.Control.Feedback type="invalid" style={{ display: 'block', color: '#f8d7da', fontSize: '13px', marginTop: '5px' }}>
                                        {Array.isArray(fieldErrors.password) ? fieldErrors.password[0] : fieldErrors.password}
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>

                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <Form.Check
                                    type="checkbox"
                                    id="rememberMe"
                                    label={<span className="text-white" style={{ fontSize: '14px' }}>Remember Me</span>}
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                />
                                <button
                                    type="button"
                                    onClick={handleToggleForgotPassword}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#d7e4d5',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        padding: '0',
                                        cursor: 'pointer',
                                        textDecoration: 'none',
                                        transition: 'color 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.color = '#e7b06d';
                                        e.currentTarget.style.textDecoration = 'underline';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.color = '#d7e4d5';
                                        e.currentTarget.style.textDecoration = 'none';
                                    }}
                                >
                                    Forgot Password?
                                </button>
                            </div>

                            <Button
                                type="submit"
                                className="w-100"
                                disabled={isSubmitting}
                                backgroundColor="#677a58"
                                color="#ffffff"
                                hoverBackgroundColor="#5a6a4a"
                                style={{
                                    backgroundColor: '#677a58',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '10px',
                                    height: '45px',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    textTransform: 'uppercase',
                                    letterSpacing: '1px',
                                    width: '100%',
                                    marginTop: 'auto',
                                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                    opacity: isSubmitting ? 0.7 : 1,
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSubmitting) {
                                        e.currentTarget.style.backgroundColor = '#5a6a4a';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSubmitting) {
                                        e.currentTarget.style.backgroundColor = '#677a58';
                                    }
                                }}
                            >
                                {isSubmitting ? 'Signing in...' : 'sign in'}
                            </Button>
                        </Form>
                        )}
                    </div>
                    
                    {/* Footer */}
                    <div className="text-center" style={{ color: '#fff', fontSize: '12px', marginTop: '15px', flexShrink: 0 }}>
                        <p className="mb-0">
                            © {new Date().getFullYear()} SDS-Customer-Portal. All rights reserved. Designed and Developed by{' '}
                            <a 
                                href="https://veeyaainnovatives.com/" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                    color: '#e7b06d',
                                    textDecoration: 'none',
                                    fontWeight: '500'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                            >
                                Veeyaa Innovatives
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerLogin;

